import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import './styles/globals.css';
import sentryConfig from './config/sentry';

/**
 * Sentry is initialised before anything renders, so a crash during the first
 * paint is still reported.
 *
 * A build with no DSN skips initialisation entirely rather than calling init()
 * with an empty string, which warns on every page load. That is the normal state
 * locally and in any environment whose Sentry project does not exist yet.
 */
if (sentryConfig.enabled) {
  Sentry.init({
    ...sentryConfig,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        /**
         * Masking posture, decided deliberately — plan §3.3.
         *
         * Inputs are masked: what the customer TYPED is the densest PII and is
         * never needed to diagnose a crash. Page text and media are kept,
         * because a replay of grey boxes tells you the shape of an interaction
         * and nothing about what the user was looking at, and diagnosability is
         * the entire point of this.
         *
         * The consequence, which belongs in the privacy policy rather than in a
         * comment: rendered contact details and uploaded damage photographs are
         * stored by Sentry in the US.
         *
         * To exclude an element, use Sentry's default selectors rather than
         * changing these flags:
         *   class="sentry-block"  — element is not recorded at all
         *   class="sentry-mask"   — element is recorded, its text is masked
         * The photo-upload preview grid is the intended home for the first.
         */
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
    ],
  });
}

/**
 * A deliberate way to prove reporting works, from the devtools console:
 *
 *   __sentryTest()
 *
 * Defined in the bundle ON PURPOSE. The Error is constructed here, so its stack
 * carries a real http(s) frame and survives the `beforeSend` filter in
 * config/sentry.js. An error thrown straight from the console does NOT — every
 * frame is `debugger eval code` or `<anonymous>` — so it is dropped silently and
 * looks exactly like Sentry being broken when it is working perfectly.
 *
 * Absent from production builds: it would otherwise be a way past that same
 * noise filter, and the free tier's error budget is small enough to care.
 */
if (sentryConfig.environment !== 'production') {
  window.__sentryTest = (message) => {
    const error = new Error(message || 'PanelGear Sentry test — raised deliberately');
    Sentry.captureException(error);
    return `sent to Sentry (${sentryConfig.environment}) — look for: ${error.message}`;
  };
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
